import { useNavigate } from 'react-router-dom';
import { z } from 'zod';

import {
	createLog,
	LogSeverity,
	Services,
	type ServicesProvider,
	type ServicesProviderScrapingConfig,
	urlSchema,
	WebsiteProvider,
} from '@ekhodealer/ekho-common';
import { Box, Form, Link, Stack, Text } from '@ekhodealer/ekho-common/rev';

import { SettingsSection } from '@common/components/SettingsSection';
import { SettingsPageContent, type TocItem } from '@common/components/SettingsTableOfContents';
import { useCurrentServicesProvider } from '@common/hooks/useCurrentServicesProvider';
import { useCurrentServicesProviderId } from '@common/hooks/useCurrentServicesProviderId';
// eslint-disable-next-line no-restricted-imports -- intentional: reusing shared inventory forms
import { useCreateInventoryScraper } from '@features/account-management/api/configuration/inventory/useCreateInventoryScraper';
import { paths } from '@config/paths';
import { SettingsSection as SettingsSectionPath } from '@config/paths/settingsSections';

import './InventorySettingsPage.scss';

// --- General tab TOC ---

const GENERAL_TOC: TocItem[] = [
	{ id: 'sources', title: 'Sources' },
];

// --- Website scraping source ---

const WEBSITE_PROVIDER_OPTIONS: { id: WebsiteProvider; label: string }[] = [
	{ id: WebsiteProvider.DEALER_SPIKE, label: 'Dealer Spike' },
	{ id: WebsiteProvider.PIXEL_MOTION, label: 'PixelMotion' },
	{ id: WebsiteProvider.DEALER_ACCELERATE, label: 'Dealer Accelerate' },
	{ id: WebsiteProvider.MOTION, label: 'Motion' },
	{ id: WebsiteProvider.OTHER, label: 'Other' },
];

const websiteScrapingSchema = z.object({
	websiteProvider: z.nativeEnum(WebsiteProvider, {
		errorMap: () => ({ message: 'Please select a website provider' }),
	}),
	allInventoryUrl: urlSchema,
});

type WebsiteScrapingFormData = z.infer<typeof websiteScrapingSchema>;

function getScrapingDefaults(
	servicesProvider: ServicesProvider | undefined | null,
): Partial<WebsiteScrapingFormData> {
	const scrapingConfig = servicesProvider?.config?.[Services.INVENTORY]?.scrapingConfig;
	if (!scrapingConfig) {
		return {
			allInventoryUrl: '',
		};
	}
	return {
		websiteProvider: scrapingConfig.websiteProvider,
		allInventoryUrl: scrapingConfig.allInventoryUrl ?? '',
	};
}

function getScrapingConfig(data: WebsiteScrapingFormData): ServicesProviderScrapingConfig {
	return {
		enabled: true,
		websiteProvider: data.websiteProvider,
		allInventoryUrl: data.allInventoryUrl.trim(),
		allInventoryUrlPaginationPattern: '',
	};
}

function WebsiteScrapingSource(): React.ReactNode {
	const { data: servicesProvider } = useCurrentServicesProvider();
	const { mutateAsync: createInventoryScraper } = useCreateInventoryScraper();

	if (!servicesProvider) {
		return null;
	}

	const handleSubmit = async (data: WebsiteScrapingFormData): Promise<{ isSuccess: boolean }> => {
		try {
			await createInventoryScraper({
				scrapingConfig: getScrapingConfig(data),
			});
			return { isSuccess: true };
		} catch (error) {
			createLog(
				'config-mgmt',
				`Failed to create scraping config: ${error instanceof Error ? error.message : String(error)}`,
				LogSeverity.ERROR,
			);
			return { isSuccess: false };
		}
	};

	return (
		<Box padding='24' background='secondary' rounding='12'>
			<Form
				name='inventory-website-scraping-source'
				schema={websiteScrapingSchema}
				defaultValues={getScrapingDefaults(servicesProvider)}
				onSubmit={handleSubmit}
				title='Non-Ekho website'
				titleLevel='subgroup'
				titleWeight='semibold'
				description='Configure how Ekho scrapes your dealership website for inventory listings. Provide the URL of your full inventory page and select your website provider so the scraper knows how to parse it.'
				showSaveAndClear>
				<Form.Field.Dropdown
					schemaField='websiteProvider'
					labelText='Website provider'
					descriptionText='Select the platform that powers your dealership website.'
					required
					options={WEBSITE_PROVIDER_OPTIONS}
					placeholder='Select provider'
					buttonVariant='outline'
				/>
				<Form.Field.Text
					schemaField='allInventoryUrl'
					labelText='Inventory page URL'
					descriptionText='The full URL of your inventory listing page with no filters applied.'
					required
					size='large'
					placeholder='https://www.example.com/inventory'
				/>
			</Form>
		</Box>
	);
}

// --- DMS reference summary ---

const dmsPlaceholderSchema = z.object({});

function DmsSourceSummary(): React.ReactNode {
	const navigate = useNavigate();
	const spId = useCurrentServicesProviderId();

	return (
		<Box padding='24' background='secondary' rounding='12'>
			<Form
				name='inventory-dms-source-summary'
				schema={dmsPlaceholderSchema}
				defaultValues={{}}
				onSubmit={async () => ({ isSuccess: false })}
				title='DMS'
				titleLevel='subgroup'
				titleWeight='semibold'
				description='Your DMS connection is another source of inventory data. DMS connection settings are managed in Integrations settings.'>
				<Link
					onPress={() =>
						navigate(
							paths.settingsSection.getHref(
								spId,
								SettingsSectionPath.SharedIntegrations,
							),
						)
					}>
					<Text size='bodySmall'>Go to Integrations settings</Text>
				</Link>
			</Form>
		</Box>
	);
}

// --- Sources section ---

function SourcesSection(): React.ReactNode {
	return (
		<Stack itemsSpacing='16'>
			<WebsiteScrapingSource />
			<DmsSourceSummary />
		</Stack>
	);
}

// --- Page ---

export function InventorySettingsPage(): React.JSX.Element {
	return (
		<div className='inventory-settings'>
			<div className='inventory-settings__content' data-settings-tab-content>
				<SettingsPageContent tocItems={GENERAL_TOC} stickyTop={64}>
					<SettingsSection
						title='Sources'
						description='Manage where Ekho pulls your inventory data from — your dealership website and DMS.'
						hideDivider>
						<SourcesSection />
					</SettingsSection>
				</SettingsPageContent>
			</div>
		</div>
	);
}
